import { Component, OnInit, inject } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { BookConsultationModalComponent } from '../../modals/book-consultation-modal/book-consultation-modal.component';
import { JourneyService } from '../services/journey.service';
import { IJourneyStep, JourneyStepStatus, JourneyStepType } from '../../../assets/types/dtoTypes';
import { CountryCode, PRICING } from "../../pages/registration-pages/pricing-page/pricing.config";
import { CountryService } from "../services/country.service";

@Component({
  selector: 'app-consultation-banner',
  templateUrl: './consultation-banner.component.html',
  styleUrls: ['./consultation-banner.component.scss'],
  standalone: true,
  imports: [NgFor, NgIf, MatIcon, TranslateModule],
})
export class ConsultationBannerComponent implements OnInit {
  private dialog = inject(MatDialog);
  private journeyService = inject(JourneyService);

  readonly StepStatus = JourneyStepStatus;
  private readonly countryService = inject(CountryService);

  country: CountryCode = 'EU';
  readonly features = [
    'consultationBanner.features.cvReview',
    'consultationBanner.features.consultation',
    'consultationBanner.features.checklist',
    'consultationBanner.features.whatsapp',
  ];

  consultationStep: IJourneyStep | null = null;
  employerMatchStep: IJourneyStep | null = null;

  ngOnInit(): void {
    this.countryService.getCountry().subscribe(country => {
      this.country = country;
    });

    this.journeyService.getMyJourney().subscribe({
      next: (journey) => {
        this.consultationStep = journey.steps.find(
          (s) => s.type === JourneyStepType.CONSULTATION
        ) ?? null;

        this.employerMatchStep = journey.steps.find(
          (s) => s.type === JourneyStepType.EMPLOYER_MATCH
        ) ?? null;
      },
    });
  }

  formatEmployerMatchPrice(): string {
    const localized = PRICING.premium[this.country];

    const locale =
      this.country === 'TN'
        ? 'fr-TN'
        : this.country === 'MA'
          ? 'fr-MA'
          : this.country === 'IN'
            ? 'en-IN'
            : 'de-DE';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: localized.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(localized.price);
  }

  get consultationStatus(): JourneyStepStatus {
    return this.consultationStep?.status ?? JourneyStepStatus.AVAILABLE;
  }

  formatBookingDate(): string {
    if (!this.consultationStep?.bookingDate) return '';
    return new Date(this.consultationStep.bookingDate).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
    });
  }

  formatBookingTime(): string {
    if (!this.consultationStep?.bookingDate) return '';
    const t = new Date(this.consultationStep.bookingDate).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return t.replace(' AM', 'am').replace(' PM', 'pm');
  }

  openModal(): void {
    this.dialog.open(BookConsultationModalComponent, {
      maxWidth: '960px',
      width: '100%',
      panelClass: 'book-consultation-panel',
    });
  }

  get formattedConsultationPrice(): string {
    const localized = PRICING.basic[this.country];

    const locale =
      this.country === 'TN'
        ? 'fr-TN'
        : this.country === 'MA'
          ? 'fr-MA'
          : this.country === 'IN'
            ? 'en-IN'
            : 'de-DE';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: localized.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(localized.price);
  }
}